import React, { useLayoutEffect, useRef, useState } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Stack,
    Typography,
    Avatar,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import WarningIcon from "@mui/icons-material/Warning";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../../contexts/AuthContext";
import UncheckInspectionDialog from "./UncheckInspectionDialog";

export function initials(person) {
    if (!person) return "";
    const first = person.first_name?.[0] || person.username?.[0] || "";
    const last = person.last_name?.[0] || "";
    return (first + last).toUpperCase();
}

export default function EquipmentCalendarGrid({
    title,
    onPrev,
    onNext,
    nextDisabled,
    columns,
    equipmentList,
    loading,
    inspectionFor,
    openIssueFor,
    onStartCheck,
    onOpenDetails,
    onResolveIssue,
    onUncheck,
}) {
    const theme = useTheme();
    const { user } = useAuth();
    const [inspectionDialog, setInspectionDialog] = useState(null);
    const containerRef = useRef(null);
    const stickyHeaderRef = useRef(null);
    const currentColumnRef = useRef(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const currentCell = currentColumnRef.current;
        if (!container || !currentCell) return;
        const stickyWidth = stickyHeaderRef.current?.offsetWidth || 0;
        const visibleWidth = container.clientWidth - stickyWidth;
        const target =
            currentCell.offsetLeft +
            currentCell.offsetWidth / 2 -
            stickyWidth -
            visibleWidth / 2;
        container.scrollLeft = Math.max(0, target);
    }, [columns]);

    const handleCellClick = (equipment, column, inspection) => {
        if (inspection) {
            if (column.isPast) {
                setInspectionDialog({ inspection, canUncheck: false });
            } else if (inspection.created_by_detail?.id === user?.id) {
                setInspectionDialog({ inspection, canUncheck: true });
            }
            return;
        }
        if (column.isCurrent) {
            onStartCheck?.(equipment);
        }
    };

    const handleConfirmUncheck = async (inspection) => {
        await onUncheck?.(inspection);
        setInspectionDialog(null);
    };

    return (
        <Box>
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={onPrev} size="small">
                        <ArrowBackIos fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                        {title}
                    </Typography>
                    <IconButton onClick={onNext} size="small" disabled={nextDisabled}>
                        <ArrowForwardIos fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            <TableContainer ref={containerRef} component={Paper} sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                ref={stickyHeaderRef}
                                sx={{
                                    position: "sticky",
                                    left: 0,
                                    zIndex: 3,
                                    backgroundColor: theme.palette.background.paper,
                                    minWidth: 160,
                                }}
                            >
                                Equipment
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.key}
                                    ref={column.isCurrent ? currentColumnRef : null}
                                    align="center"
                                    sx={{
                                        minWidth: 44,
                                        fontWeight: column.isCurrent ? 700 : 400,
                                        color: column.isCurrent ? "primary.main" : undefined,
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {equipmentList.map((equipment) => {
                            const openIssue = openIssueFor(equipment.id);
                            return (
                                <TableRow key={equipment.id}>
                                    <TableCell
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 2,
                                            backgroundColor: theme.palette.background.paper,
                                            cursor: "pointer",
                                            textDecoration: openIssue ? "line-through" : "none",
                                            color: openIssue ? "error.main" : undefined,
                                        }}
                                        onClick={() => onOpenDetails?.(equipment)}
                                    >
                                        <Typography variant="body2" fontWeight={600} noWrap>
                                            {equipment.nickname}
                                        </Typography>
                                    </TableCell>
                                    {columns.map((column) => {
                                        const inspection = inspectionFor(equipment.id, column.key);
                                        if (openIssue && column.isCurrent) {
                                            return (
                                                <TableCell key={column.key} align="center">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => onResolveIssue?.(equipment, openIssue)}
                                                    >
                                                        <WarningIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            );
                                        }
                                        const ownInspection = inspection && inspection.created_by_detail?.id === user?.id;
                                        const clickable =
                                            (inspection && column.isPast) ||
                                            ownInspection ||
                                            (column.isCurrent && !inspection);
                                        return (
                                            <TableCell key={column.key} align="center">
                                                {inspection ? (
                                                    <Tooltip
                                                        title={
                                                            inspection.created_by_detail?.full_name ||
                                                            inspection.created_by_detail?.username ||
                                                            ""
                                                        }
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            disabled={!clickable}
                                                            onClick={() => handleCellClick(equipment, column, inspection)}
                                                        >
                                                            <Avatar
                                                                src={inspection.created_by_detail?.profile_image || undefined}
                                                                sx={{ width: 24, height: 24, fontSize: "0.7rem" }}
                                                            >
                                                                {initials(inspection.created_by_detail)}
                                                            </Avatar>
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : column.isPast ? (
                                                    <Tooltip title="Not checked">
                                                        <span>
                                                            <IconButton size="small" disabled>
                                                                <EventBusyIcon fontSize="small" color="warning" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <IconButton
                                                        size="small"
                                                        disabled={!clickable}
                                                        onClick={() => handleCellClick(equipment, column, inspection)}
                                                    >
                                                        <CheckBoxOutlineBlankIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={22} />
                    </Box>
                )}
            </TableContainer>

            <UncheckInspectionDialog
                open={!!inspectionDialog}
                onClose={() => setInspectionDialog(null)}
                inspection={inspectionDialog?.inspection}
                canUncheck={inspectionDialog?.canUncheck}
                onConfirm={handleConfirmUncheck}
            />
        </Box>
    );
}
